/**
 * TBMuktBharat Create Competition Page
 *
 * Any authenticated user may create a competition. New competitions are saved
 * as DRAFT and become visible once an admin publishes them. Dates are entered
 * as date-only values (YYYY-MM-DD); competitionService converts them to full
 * ISO datetimes before sending (LocalDateTime on the backend).
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { Trophy, Loader2 } from 'lucide-react'
import competitionService, { CompetitionCreateRequest, CompetitionType } from '@/services/competitionService'

const COMPETITION_TYPES: { value: CompetitionType; label: string }[] = [
  { value: 'POSTER', label: 'Poster' },
  { value: 'QUIZ', label: 'Quiz' },
  { value: 'ESSAY', label: 'Essay' },
  { value: 'DRAWING', label: 'Drawing' },
  { value: 'SLOGAN', label: 'Slogan' }
]

const CreateCompetitionPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState<CompetitionCreateRequest>({
    title: '',
    description: '',
    type: 'POSTER',
    startDate: '',
    endDate: '',
    resultDate: '',
    maxParticipants: undefined,
    prizeDescription: '',
    rules: '',
    guidelines: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // The service converts date-only strings to full ISO datetimes. Strip
      // empty strings so the backend treats them as null (no deadline set).
      const competition = await competitionService.createCompetition({
        ...formData,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        resultDate: formData.resultDate || undefined
      })
      navigate(`/competitions/${competition.id}`)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create competition')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxParticipants'
        ? value ? Number(value) : undefined
        : value
    }))
  }

  return (
    <GovLayout>
      <GovPageTitle
        title="Create Competition"
        subtitle="Launch a TB awareness competition (saved as draft until an admin publishes it)"
        icon={Trophy}
      />

      <GovCard>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Competition Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter competition title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your competition"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Competition Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {COMPETITION_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Result Date
              </label>
              <input
                type="date"
                name="resultDate"
                value={formData.resultDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Max Participants */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Participants
            </label>
            <input
              type="number"
              name="maxParticipants"
              value={formData.maxParticipants || ''}
              onChange={handleChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 500"
            />
          </div>

          {/* Prize Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prize Description
            </label>
            <textarea
              name="prizeDescription"
              value={formData.prizeDescription}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., First Prize: ₹10,000 | Second Prize: ₹5,000"
            />
          </div>

          {/* Rules */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rules
            </label>
            <textarea
              name="rules"
              value={formData.rules}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Competition rules and eligibility"
            />
          </div>

          {/* Guidelines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Guidelines
            </label>
            <textarea
              name="guidelines"
              value={formData.guidelines}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Submission guidelines (format, file types, word limit, etc.)"
            />
          </div>

          {/* Info note */}
          <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Your competition will be saved as a <strong>draft</strong>. It becomes
            visible to participants once an admin publishes it.
          </p>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/competitions')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.title}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Competition'
              )}
            </button>
          </div>
        </form>
      </GovCard>
    </GovLayout>
  )
}

export default React.memo(CreateCompetitionPage)